import { FC } from "react";
import { Box, Stack, Typography, IconButton } from "@mui/material";
import { IconChevronRight, IconClose } from "@/assets/icons";
import { background, divider, icon, text } from "@/theme/colors";
import { spacing, rounded } from "@/theme/variables";
import { typography } from "@/theme/typography";

type StudyCardData = {
  title: string;
};

/** StudyTaskCard container with header, divider, content (spec-compliant) */
export const StudyTaskCard: FC<{
  title: string;
  accentColor: string;
  bgColor?: string;
  titleColor?: string;
  card: StudyCardData;
  leftIcon?: React.ReactNode;
  onClick: () => void;
  onClose?: () => void;
}> = ({ title, accentColor, bgColor = background.white, titleColor = text.primary, card, leftIcon, onClick, onClose }) => (
  <Stack
    sx={{
      borderRadius: rounded.xs,
      border: `1px solid ${accentColor}`,
      bgcolor: bgColor,
      cursor: "pointer",
      gap: spacing.xs,
      px: spacing.sm,
      py: `calc(${spacing.xxs} + ${spacing.xs})`,
    }}
    onClick={onClick}
  >
    <Stack direction="row" alignItems="center" height={24}>
      {leftIcon && <Box sx={{ display: "flex", alignItems: "center" }}>{leftIcon}</Box>}
      <Typography sx={{ ...typography.body14Bold, color: titleColor }}>{title}</Typography>

      {onClose && (
        <Box sx={{ ml: "auto" }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            <IconClose color={icon.default} width={24} height={24} />
          </IconButton>
        </Box>
      )}
    </Stack>

    {/* Divider */}
    <Box sx={{ borderTop: `1px solid ${divider.high}` }} />

    {/* Content: subject info + chevron */}
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Stack sx={{ flex: 1, gap: spacing.xxs }}>
        <Typography sx={{ ...typography.body14Semi, color: text.primary }}>{card.title}</Typography>
      </Stack>
      <Box sx={{ display: "flex", flexShrink: 0 }}>
        <IconButton size="small" tabIndex={-1}>
          <IconChevronRight fontSize={24} color={icon.default} />
        </IconButton>
      </Box>
    </Stack>
  </Stack>
);
